﻿// src/modules/analytics.js — Analytics dashboard, charts, feature flags, provider profile
import { state } from '../state.js';
import { ANALYTICS_USE_MOCK, MAX_RECENT_TYPES, APP_VERSION } from '../utils/constants.js';
import { apiFetch, getSessionToken } from '../utils/api.js';
import { escapeHtml, showView, navigateTo, showToast, setStatus } from '../utils/dom.js';
import { logger, apiFetchWithRefresh } from './debug.js';
import { ProfileStore } from '../stores/profile-store.js';
import { getCachedVisibleSections, getUserMetaCache } from './auth.js';
import { openProfileMgmt, editProfile, showResetPin } from './profiles.js';
import { updateConsentVisibility } from './consent.js';
import { updateTrialBanner } from './trial.js';

let analyticsData = null;
let analyticsCharts = {};
let analyticsViewAsHash = null;
let analyticsLastLoadTime = null;
let analyticsFeatureFlags = null;
let analyticsAutoRefreshTimer = null;

export function showAnalytics() {
    // Guard: check cached visibleSections before navigating
    var sections = getCachedVisibleSections();
    if (sections && !sections.analytics) {
        logger.warn('ANALYTICS', 'Navigation blocked â€” analytics section hidden by visibleSections');
        return;
    }
    navigateTo('analytics');
    // Bug #5 fix: hash-based navigation for view persistence across refresh
    location.hash = '#analytics';
    analyticsViewAsHash = null;
    analyticsResetState();
    // Bug #2 fix: auto-scope doctor profiles â€” non-admin profiles only see their own data
    if (currentProfileId && !isProfileAdmin) {
        loadAnalyticsDashboard(currentProfileId);
    } else {
        loadAnalyticsDashboard();
    }
}

export function hideAnalytics() {
    navigateTo('record');
    // Bug #5 fix: clear hash when leaving analytics
    if (location.hash === '#analytics') {
        history.replaceState(null, '', location.pathname + location.search);
    }
    analyticsViewAsHash = null;
    destroyAnalyticsCharts();
    analyticsStopAutoRefresh();
    analyticsResetState();
}

export function analyticsResetState() {
    // Complete state reset on view transition (code-patterns.md)
    const content = document.getElementById('analyticsContent');
    const loading = document.getElementById('analyticsLoading');
    const error = document.getElementById('analyticsError');
    const empty = document.getElementById('analyticsEmpty');
    const lastRefreshed = document.getElementById('analyticsLastRefreshed');
    const ffSection = document.getElementById('analyticsFeatureFlagsSection');
    const ffContent = document.getElementById('featureFlagsContent');
    const ffLoading = document.getElementById('featureFlagsLoading');
    const ffError = document.getElementById('featureFlagsError');
    const viewAsBanner = document.getElementById('analyticsViewAsBanner');
    const streakSection = document.getElementById('analyticsStreakSection');
    const comparisonSection = document.getElementById('analyticsComparisonSection');
    const officeSelect = document.getElementById('analyticsOfficeSelect');

    if (content) content.classList.add('hidden');
    if (loading) { loading.classList.remove('hidden'); loading.setAttribute('aria-busy', 'true'); }
    if (error) error.classList.add('hidden');
    if (empty) empty.classList.add('hidden');
    if (lastRefreshed) lastRefreshed.classList.add('hidden');
    if (ffSection) ffSection.classList.add('hidden');
    if (ffContent) ffContent.classList.add('hidden');
    if (ffLoading) ffLoading.classList.remove('hidden');
    if (ffError) ffError.classList.add('hidden');
    if (viewAsBanner) viewAsBanner.classList.add('hidden');
    if (streakSection) streakSection.classList.add('hidden');
    if (comparisonSection) comparisonSection.classList.add('hidden');
    if (officeSelect) officeSelect.classList.add('hidden');

    // Reset trend arrows
    ['analyticsNoteCountTrend', 'analyticsTimeSavedTrend', 'analyticsCostSavingsTrend', 'analyticsAvgPerNoteTrend'].forEach(function(id) {
        var el = document.getElementById(id);
        if (el) el.classList.add('hidden');
    });

    destroyAnalyticsCharts();
    analyticsData = null;
    analyticsFeatureFlags = null;
    window._lastAnalyticsData = null;
}

export function analyticsStopAutoRefresh() {
    if (analyticsAutoRefreshTimer) {
        clearInterval(analyticsAutoRefreshTimer);
        analyticsAutoRefreshTimer = null;
    }
}

export function analyticsStartAutoRefresh() {
    analyticsStopAutoRefresh();
    // Auto-refresh every 5 minutes
    analyticsAutoRefreshTimer = setInterval(function() {
        loadAnalyticsDashboard(null, true);
    }, 5 * 60 * 1000);
}

export function analyticsUpdateLastRefreshed() {
    analyticsLastLoadTime = Date.now();
    const el = document.getElementById('analyticsLastRefreshed');
    if (el) {
        el.textContent = 'Just now';
        el.classList.remove('hidden');
    }
}

// Update the "last refreshed" text every 30 seconds
setInterval(function() {
    if (!analyticsLastLoadTime) return;
    const el = document.getElementById('analyticsLastRefreshed');
    if (!el || el.classList.contains('hidden')) return;
    const diffMs = Date.now() - analyticsLastLoadTime;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) {
        el.textContent = 'Just now';
    } else if (diffMin === 1) {
        el.textContent = '1 min ago';
    } else {
        el.textContent = diffMin + ' min ago';
    }
}, 30000);

export function viewAsOffice(emailHash) {
    analyticsViewAsHash = emailHash;
    analyticsShowLoading();
    loadAnalyticsDashboard();
}

export function exitViewAsMode() {
    analyticsViewAsHash = null;
    analyticsShowLoading();
    loadAnalyticsDashboard();
}

export function analyticsShowLoading() {
    const content = document.getElementById('analyticsContent');
    const loading = document.getElementById('analyticsLoading');
    const error = document.getElementById('analyticsError');
    const empty = document.getElementById('analyticsEmpty');
    if (content) content.classList.add('hidden');
    if (loading) { loading.classList.remove('hidden'); loading.setAttribute('aria-busy', 'true'); }
    if (error) error.classList.add('hidden');
    if (empty) empty.classList.add('hidden');
}

export function analyticsShowError(message) {
    const content = document.getElementById('analyticsContent');
    const loading = document.getElementById('analyticsLoading');
    const error = document.getElementById('analyticsError');
    const empty = document.getElementById('analyticsEmpty');
    const errorMsg = document.getElementById('analyticsErrorMessage');
    if (content) content.classList.add('hidden');
    if (loading) { loading.classList.add('hidden'); loading.removeAttribute('aria-busy'); }
    if (empty) empty.classList.add('hidden');
    if (error) error.classList.remove('hidden');
    if (errorMsg) errorMsg.textContent = message || 'Failed to load analytics data.';
}

export function analyticsShowEmpty() {
    const content = document.getElementById('analyticsContent');
    const loading = document.getElementById('analyticsLoading');
    const error = document.getElementById('analyticsError');
    const empty = document.getElementById('analyticsEmpty');
    if (content) content.classList.add('hidden');
    if (loading) { loading.classList.add('hidden'); loading.removeAttribute('aria-busy'); }
    if (error) error.classList.add('hidden');
    if (empty) empty.classList.remove('hidden');
}

export function analyticsShowContent() {
    const content = document.getElementById('analyticsContent');
    const loading = document.getElementById('analyticsLoading');
    const error = document.getElementById('analyticsError');
    const empty = document.getElementById('analyticsEmpty');
    if (content) content.classList.remove('hidden');
    if (loading) { loading.classList.add('hidden'); loading.removeAttribute('aria-busy'); }
    if (error) error.classList.add('hidden');
    if (empty) empty.classList.add('hidden');
}

export function onAnalyticsProfileChange() {
    const select = document.getElementById('analyticsProfileSelect');
    const profileId = select.value;
    if (profileId) {
        loadAnalyticsDashboard(profileId);
    } else {
        loadAnalyticsDashboard();
    }
}

export async function loadAnalyticsDashboard(profileId, isAutoRefresh) {
    if (!isAutoRefresh) {
        analyticsShowLoading();
    }
    try {
        let data;
        if (ANALYTICS_USE_MOCK) {
            // Mock data mode â€” generate realistic data without backend
            data = generateMockAnalyticsData();
        } else {
            const headers = {};
            if (profileId) {
                headers['X-Profile-Id'] = profileId;
            }
            let url = '/analytics/dashboard';
            if (analyticsViewAsHash) {
                url += '?viewAs=' + encodeURIComponent(analyticsViewAsHash);
            }
            const res = await apiFetch(url, { headers });
            if (!res.ok) {
                const errData = await res.json().catch(function() { return {}; });
                if (res.status === 403 && errData.error === 'Analytics not available yet') {
                    // Bug #1 fix: distinguish initial load vs profile-filter 403
                    if (profileId) {
                        // Profile-filter 403: show inline error, DON'T crash the view
                        analyticsShowError('Analytics is not available for this profile yet.');
                        return;
                    }
                    // Initial load 403: feature disabled for this user â€” hide nav gracefully
                    const navBtn = document.getElementById('navAnalytics');
                    if (navBtn) navBtn.classList.add('hidden');
                    hideAnalytics();
                    return;
                }
                throw new Error(errData.error || 'Failed to load analytics');
            }
            data = await res.json();
        }
        analyticsData = data;

        // Check for empty data
        const totalNotes = analyticsGetTotalNotes(analyticsData);
        if (totalNotes === 0) {
            analyticsShowEmpty();
            return;
        }

        analyticsShowContent();
        renderAnalyticsDashboard(analyticsData, profileId);
        renderEnhancedAnalytics(analyticsData);
        analyticsUpdateLastRefreshed();
        analyticsStartAutoRefresh();

        // Feature flags removed from analytics â€” managed in Admin Dashboard
    } catch (err) {
        logger.error('ANALYTICS', 'Failed to load analytics dashboard', err, { context: 'loadAnalyticsDashboard' });
        analyticsShowError(err.message || 'Failed to load analytics data. Please try again.');
    }
}

export function analyticsGetTotalNotes(data) {
    if (!data) return 0;
    if (data.role === 'super_admin' && data.totals) return data.totals.totalNotes || 0;
    if (data.officeTotals) return data.officeTotals.totalNotes || 0;
    if (data.stats) return data.stats.totalNotes || 0;
    return 0;
}

// Wire retry buttons on DOM ready
document.addEventListener('DOMContentLoaded', function() {
    const retryBtn = document.getElementById('btnAnalyticsRetry');
    if (retryBtn) {
        retryBtn.addEventListener('click', function() { loadAnalyticsDashboard(); });
    }
    const ffRetryBtn = document.getElementById('btnFeatureFlagsRetry');
    if (ffRetryBtn) {
        ffRetryBtn.addEventListener('click', function() { loadFeatureFlags(); });
    }
});

export function renderAnalyticsDashboard(data, selectedProfileId) {
    const role = data.role;
    const isSuperAdmin = !data.viewingAs && analyticsViewAsHash === null && role === 'super_admin';
    const isViewingAs = !!data.viewingAs;

    const roleLabels = {
        super_admin: 'Analytics',
        office_admin: 'Office Dashboard',
        doctor: 'My Analytics'
    };
    document.getElementById('analyticsRoleLabel').textContent = roleLabels[isSuperAdmin ? 'super_admin' : role] || 'Dashboard';

    // Show/hide viewAs banner â€” REMOVED: the office dropdown handles navigation
    const viewAsBanner = document.getElementById('analyticsViewAsBanner');
    if (viewAsBanner) {
        if (analyticsViewAsHash && data.viewingAs) {
            var officeName = data.viewingAs.clinicName || 'Office ' + analyticsViewAsHash.substring(0, 6);
            viewAsBanner.innerHTML = '<span>Viewing as <strong>' + escapeHtml(officeName) + '</strong></span>'
                + ' <button class="btn btn-sm btn-outline-secondary ms-2" data-action="exit-view-as-mode">Exit View</button>';
            viewAsBanner.classList.remove('hidden');
        } else {
            viewAsBanner.classList.add('hidden');
        }
    }

    // Show/hide office dropdown for super admin (item 2)
    var officeSelect = document.getElementById('analyticsOfficeSelect');
    if (officeSelect) {
        if (isSuperAdmin && data.offices && data.offices.length > 1) {
            officeSelect.classList.remove('hidden');
            officeSelect.innerHTML = '<option value="">All Offices</option>';
            data.offices.forEach(function(office) {
                var opt = document.createElement('option');
                opt.value = office.emailHash;
                opt.textContent = escapeHtml(office.clinicName || 'Office ' + office.emailHash.substring(0, 6));
                officeSelect.appendChild(opt);
            });
            // Restore selection if in viewAs mode
            if (analyticsViewAsHash) {
                officeSelect.value = analyticsViewAsHash;
            }
            // Wire onchange via event listener (avoid inline from server data)
            officeSelect.onchange = function() {
                var hash = officeSelect.value;
                if (hash) {
                    viewAsOffice(hash);
                } else {
                    exitViewAsMode();
                }
            };
        } else {
            officeSelect.classList.add('hidden');
        }
    }

    // Show/hide profile selector for office_admin or viewAs mode
    const profileSelect = document.getElementById('analyticsProfileSelect');
    if ((role === 'office_admin' || isViewingAs) && data.doctors && data.doctors.length > 1) {
        profileSelect.classList.remove('hidden');
        profileSelect.innerHTML = '<option value="">All Profiles (Office View)</option>';
        data.doctors.forEach(function(doc, idx) {
            const opt = document.createElement('option');
            opt.value = doc.profileId;
            var dName = doc.displayName || '';
            if (!dName || /^[a-f0-9]{6,}$/i.test(dName)) dName = 'Profile ' + (idx + 1);
            opt.textContent = dName;
            profileSelect.appendChild(opt);
        });
        // Restore selection if a profile was previously selected
        if (selectedProfileId) {
            profileSelect.value = selectedProfileId;
        }
    } else {
        profileSelect.classList.add('hidden');
    }

    // Show/hide AWS cost elements based on role (super_admin only, NOT in mimic/viewAs mode)
    // REDESIGN: Hide cost from analytics â€” cost data lives in Admin Dashboard now
    const awsCostCard = document.getElementById('analyticsAwsCostCard');
    const costSection = document.getElementById('analyticsCostSection');
    if (awsCostCard) awsCostCard.classList.add('hidden');
    if (costSection) costSection.classList.add('hidden');

    // Apply section visibility for non-super-admin (from visibleSections in response)
    if (!isSuperAdmin && data.visibleSections) {
        applyAnalyticsSectionVisibility(data.visibleSections);
    } else if (isSuperAdmin) {
        // Super admin sees same view as office admin â€” no cost, no flags, no manual vs AI
        analyticsShowAllSections();
    }

    // Feature flags section: show for super admin and load flags
    const ffSection = document.getElementById('analyticsFeatureFlagsSection');
    if (ffSection) {
        if (isSuperAdmin) {
            ffSection.classList.remove('hidden');
            loadFeatureFlags();
        } else {
            ffSection.classList.add('hidden');
        }
    }

    // Manual vs AI Charting, Recording Activity, Time Saved â€” hide from analytics
    // These are misleading vanity metrics. Real data is in Admin Dashboard.
    const compSection = document.getElementById('analyticsComparisonSection');
    const recActivitySection = document.getElementById('analyticsRecordingActivitySection');
    if (compSection) compSection.classList.add('hidden');
    if (recActivitySection) recActivitySection.classList.remove('hidden');

    // Compute period data for trend arrows and this-week values
    var periods = analyticsGetPeriods(data, isSuperAdmin);
    var weeklyData = periods.weekly || [];

    // Bug #4 fix: Compute filtered totals from weekly data when a date range is active (not "all")
    var filteredStats = null;
    if (analyticsDateRange === 'today' && data.todaySummary) {
        // "Today" uses todaySummary directly â€” avoids inflated counts from weekly buckets
        filteredStats = {
            totalNotes: data.todaySummary.notesToday || 0,
            totalTimeSavedMinutes: data.todaySummary.timeSavedToday || 0,
            costSavings: (data.todaySummary.timeSavedToday || 0) > 0 ? Math.round((data.todaySummary.timeSavedToday || 0) * 2.5) : 0,
            totalCost: 0,
            avgTimeSavedPerNote: (data.todaySummary.notesToday || 0) > 0
                ? (data.todaySummary.timeSavedToday || 0) / (data.todaySummary.notesToday || 0)
                : 0
        };
    } else if (analyticsDateRange && analyticsDateRange !== 'all') {
        var filteredForStats = filterDataByDateRange(data);
        var filteredWeekly = filteredForStats.weeklyNotes || [];
        // For super admin, aggregate across all offices
        if (isSuperAdmin && filteredForStats.offices) {
            var aggPeriods = aggregateOfficesPeriods(filteredForStats.offices);
            filteredWeekly = aggPeriods.weekly || [];
        }
        if (filteredWeekly.length > 0) {
            var fTotalNotes = filteredWeekly.reduce(function(s, w) { return s + (w.noteCount || 0); }, 0);
            var fTotalTimeSaved = filteredWeekly.reduce(function(s, w) { return s + (w.totalTimeSavedMinutes || 0); }, 0);
            var fTotalCost = filteredWeekly.reduce(function(s, w) { return s + (w.totalCost || 0); }, 0);
            filteredStats = {
                totalNotes: fTotalNotes,
                totalTimeSavedMinutes: fTotalTimeSaved,
                costSavings: fTotalNotes > 0 ? Math.round(fTotalTimeSaved * 2.5) : 0,
                totalCost: fTotalCost,
                avgTimeSavedPerNote: fTotalNotes > 0 ? fTotalTimeSaved / fTotalNotes : 0
            };
        }
    }

    // If date filter is active but no filtered data available, fall back to todaySummary if available
    if (!filteredStats && analyticsDateRange && analyticsDateRange !== 'all') {
        if (data.todaySummary && (data.todaySummary.notesToday || 0) > 0) {
            // Use todaySummary as minimum — covers edge case where weekly bucket hasn't been created yet
            filteredStats = {
                totalNotes: data.todaySummary.notesToday || 0,
                totalTimeSavedMinutes: data.todaySummary.timeSavedToday || 0,
                costSavings: (data.todaySummary.timeSavedToday || 0) > 0 ? Math.round((data.todaySummary.timeSavedToday || 0) * 2.5) : 0,
                totalCost: 0,
                avgTimeSavedPerNote: (data.todaySummary.notesToday || 0) > 0
                    ? (data.todaySummary.timeSavedToday || 0) / (data.todaySummary.notesToday || 0)
                    : 0
            };
        } else {
            filteredStats = { totalNotes: 0, totalTimeSavedMinutes: 0, costSavings: 0, avgTimeSavedPerNote: 0 };
        }
    }

    // Render stat cards based on role (items 4-5: role-aware content + trend arrows)
    if (isSuperAdmin) {
        renderAnalyticsStats(filteredStats || data.totals, false, weeklyData, data);
    } else if (role === 'office_admin' || isViewingAs) {
        if (selectedProfileId && data.doctors && data.doctors.length > 0) {
            var selectedDoc = data.doctors.find(function(d) { return d.profileId === selectedProfileId; });
            if (selectedDoc) {
                var docStats = {
                    totalNotes: selectedDoc.noteCount || 0,
                    totalTimeSavedMinutes: selectedDoc.totalTimeSavedMinutes || 0,
                    costSavings: selectedDoc.costSavings || 0,
                    avgTimeSavedPerNote: selectedDoc.avgTimeSavedPerNote || 0
                };
                renderAnalyticsStats(filteredStats || docStats, false, weeklyData, data);
            } else {
                renderAnalyticsStats(filteredStats || data.officeTotals, false, weeklyData, data);
            }
        } else {
            renderAnalyticsStats(filteredStats || data.officeTotals, false, weeklyData, data);
        }
    } else if (role === 'doctor') {
        renderAnalyticsStats(filteredStats || data.stats, false, weeklyData, data);
    }

    // Render manual vs AI comparison (item 6) â€” for doctors and office admins
    renderAnalyticsComparison(data, isSuperAdmin);

    // Render charts
    renderAnalyticsCharts(data);

    // Render per-doctor/office grids
    renderAnalyticsBreakdowns(data);
}

// Helper: get periods from data for any role
export function analyticsGetPeriods(data, isSuperAdmin) {
    if (isSuperAdmin) {
        return aggregateOfficesPeriods(data.offices);
    }
    return { weekly: data.weeklyNotes || [], monthly: data.monthlyNotes || [] };
}

// Render trend arrow on a stat card
export function renderTrendArrow(elementId, currentVal, previousVal) {
    var el = document.getElementById(elementId);
    if (!el) return;
    if (typeof currentVal !== 'number' || typeof previousVal !== 'number' || previousVal === 0) {
        el.classList.add('hidden');
        return;
    }
    var diff = currentVal - previousVal;
    var pct = Math.round((diff / previousVal) * 100);
    el.classList.remove('hidden', 'trend-up', 'trend-down', 'trend-flat');
    if (diff > 0) {
        el.classList.add('trend-up');
        el.textContent = '\u2191 ' + Math.abs(pct) + '% vs last week';
    } else if (diff < 0) {
        el.classList.add('trend-down');
        el.textContent = '\u2193 ' + Math.abs(pct) + '% vs last week';
    } else {
        el.classList.add('trend-flat');
        el.textContent = '\u2192 Same as last week';
    }
}

// Render weekly streak (item 7)
// renderAnalyticsStreak â€” removed (streak section removed from analytics)

// Render manual vs AI comparison bar (item 6) â€” REMOVED: misleading vanity metric
export function renderAnalyticsComparison(data, isSuperAdmin) {
    var section = document.getElementById('analyticsComparisonSection');
    if (!section) return;
    // Always hide â€” this section is removed from analytics
    section.classList.add('hidden');
}

export function applyAnalyticsSectionVisibility(visibleSections) {
    // visibleSections: { noteCount: bool, timeSaved: bool, costSavings: bool, ... }
    const sectionMap = {
        noteCount: 'analyticsNoteCountCard',
        timeSaved: 'analyticsTimeSavedCard',
        costSavings: 'analyticsCostSavingsCard',
        avgPerNote: 'analyticsAvgPerNoteCard',
        recordingActivityChart: 'analyticsRecordingActivitySection',
        perDoctorBreakdown: 'analyticsDoctorsSection'
    };
    Object.keys(sectionMap).forEach(function(key) {
        const el = document.getElementById(sectionMap[key]);
        if (!el) return;
        if (visibleSections[key] === false) {
            el.classList.add('hidden');
            el.setAttribute('data-flag-hidden', 'true');
        } else {
            // Only remove hidden if not hidden by other logic (e.g., doctor section hidden when no doctors)
            if (el.getAttribute('data-flag-hidden') === 'true') {
                el.removeAttribute('data-flag-hidden');
            }
            // Don't force-remove hidden here â€” other logic may legitimately hide it
        }
    });
}

export function analyticsShowAllSections() {
    var sectionIds = [
        'analyticsNoteCountCard', 'analyticsTimeSavedCard', 'analyticsCostSavingsCard',
        'analyticsAvgPerNoteCard',
        'analyticsRecordingActivitySection'
    ];
    sectionIds.forEach(function(id) {
        var el = document.getElementById(id);
        if (el) {
            el.removeAttribute('data-flag-hidden');
            // stat cards managed by renderAnalyticsStats, chart sections always visible for super admin
            if (id.indexOf('Section') > -1) {
                el.classList.remove('hidden');
            }
        }
    });
}

export function renderAnalyticsStats(stats, showCost, weeklyData, fullData) {
    if (!stats) return;
    const noteCountEl = document.getElementById('analyticsNoteCount');
    const timeSavedEl = document.getElementById('analyticsTimeSaved');
    const costSavingsEl = document.getElementById('analyticsCostSavings');
    const awsCostEl = document.getElementById('analyticsAwsCost');
    const avgPerNoteEl = document.getElementById('analyticsAvgPerNote');
    const avgPerNoteCard = document.getElementById('analyticsAvgPerNoteCard');

    // Animated countup for stat values
    if (noteCountEl) animateStatValue(noteCountEl, stats.totalNotes || 0, false);
    if (timeSavedEl) {
        const timeSaved = stats.totalTimeSavedMinutes || 0;
        if (timeSaved >= 60) {
            timeSavedEl.textContent = (timeSaved / 60).toFixed(1) + ' hrs';
        } else {
            timeSavedEl.textContent = Math.round(timeSaved) + ' min';
        }
    }
    if (costSavingsEl) costSavingsEl.textContent = '$' + (stats.costSavings || 0).toLocaleString();
    // Hide cost savings card when $0
    var costSavingsCard = document.getElementById('analyticsCostSavingsCard');
    if (costSavingsCard) {
        if ((stats.costSavings || 0) === 0) {
            costSavingsCard.classList.add('hidden');
        } else {
            costSavingsCard.classList.remove('hidden');
        }
    }
    if (showCost && awsCostEl) awsCostEl.textContent = '$' + (stats.totalCost || 0).toFixed(2);

    // Show avg per note card (item 4: avgTimeSavedPerNote)
    if (avgPerNoteEl && avgPerNoteCard) {
        var avgPerNote = stats.avgTimeSavedPerNote || 0;
        if (avgPerNote === 0 && stats.totalNotes > 0 && stats.totalTimeSavedMinutes > 0) {
            avgPerNote = stats.totalTimeSavedMinutes / stats.totalNotes;
        }
        if (avgPerNote > 0) {
            avgPerNoteEl.textContent = avgPerNote.toFixed(1) + ' min';
            avgPerNoteCard.classList.remove('hidden');
        } else {
            avgPerNoteCard.classList.add('hidden');
        }
    }

    // Update stat card labels with "this week" context for non-super-admin
    var noteLabel = document.getElementById('analyticsNoteCountLabel');
    var timeLabel = document.getElementById('analyticsTimeSavedLabel');
    var costLabel = document.getElementById('analyticsCostSavingsLabel');
    var avgLabel = document.getElementById('analyticsAvgPerNoteLabel');
    if (noteLabel) noteLabel.textContent = 'Total Notes';
    if (timeLabel) timeLabel.textContent = 'Time Saved';
    if (costLabel) costLabel.textContent = 'Cost Savings (est.)';
    if (avgLabel) avgLabel.textContent = 'Avg Per Note';

    // Trend arrows (item 5) â€” compare last two weeks
    if (weeklyData && weeklyData.length >= 2) {
        var lastWeek = weeklyData[weeklyData.length - 1];
        var prevWeek = weeklyData[weeklyData.length - 2];
        renderTrendArrow('analyticsNoteCountTrend', lastWeek.noteCount || 0, prevWeek.noteCount || 0);
        renderTrendArrow('analyticsTimeSavedTrend', lastWeek.totalTimeSavedMinutes || 0, prevWeek.totalTimeSavedMinutes || 0);
        // Cost savings trend (compute from note counts * hourlyRate factor)
        var lastSavings = (lastWeek.totalTimeSavedMinutes || 0);
        var prevSavings = (prevWeek.totalTimeSavedMinutes || 0);
        renderTrendArrow('analyticsCostSavingsTrend', lastSavings, prevSavings);
    } else {
        // Hide all trends if insufficient data
        ['analyticsNoteCountTrend', 'analyticsTimeSavedTrend', 'analyticsCostSavingsTrend', 'analyticsAvgPerNoteTrend'].forEach(function(id) {
            var el = document.getElementById(id);
            if (el) el.classList.add('hidden');
        });
    }
}

export function animateStatValue(el, targetValue, isCurrency) {
    if (!el || targetValue === 0) {
        el.textContent = isCurrency ? '$0' : '0';
        return;
    }
    const duration = 400; // ms
    const animStart = performance.now();
    const startValue = 0;
    function step(now) {
        const elapsed = now - animStart;
        const progress = Math.min(elapsed / duration, 1);
        // Ease-out quad
        const eased = 1 - (1 - progress) * (1 - progress);
        const current = Math.round(startValue + (targetValue - startValue) * eased);
        el.textContent = isCurrency ? '$' + current.toLocaleString() : current.toLocaleString();
        if (progress < 1) {
            requestAnimationFrame(step);
        }
    }
    requestAnimationFrame(step);
}

export function renderAnalyticsCharts(data) {
    destroyAnalyticsCharts();

    // Bug #4 fix: Apply date range filter before computing chart data
    var filteredData = filterDataByDateRange(data);

    const role = filteredData.role;
    const isSuperAdmin = !filteredData.viewingAs && analyticsViewAsHash === null && role === 'super_admin';
    const isViewingAs = !!filteredData.viewingAs;
    let periods;
    if (isSuperAdmin) {
        periods = aggregateOfficesPeriods(filteredData.offices);
    } else if (role === 'office_admin') {
        periods = { weekly: filteredData.weeklyNotes || [], monthly: filteredData.monthlyNotes || [] };
    } else {
        periods = { weekly: filteredData.weeklyNotes || [], monthly: filteredData.monthlyNotes || [] };
    }

    // Auto-determine aggregation: 'all' â†’ monthly, everything else â†’ weekly
    const periodData = analyticsDateRange === 'all' ? periods.monthly : periods.weekly;
    if (!periodData || periodData.length === 0) return;

    const labels = periodData.map(p => p.period);
    const noteCounts = periodData.map(p => p.noteCount);
    const timeSaved = periodData.map(p => p.totalTimeSavedMinutes);

    // Combined chart: Notes (bar) + Time Saved (line) â€” hide section when no data
    const notesCtx = document.getElementById('chartNotes');
    const notesSection = document.getElementById('analyticsRecordingActivitySection');
    const hasNotes = noteCounts.length > 0 && noteCounts.some(function(n) { return n > 0; });
    const hasTime = timeSaved.length > 0 && timeSaved.some(function(n) { return n > 0; });
    if (!hasNotes && !hasTime) {
        if (notesSection) notesSection.classList.add('hidden');
    } else if (notesCtx) {
        if (notesSection) notesSection.classList.remove('hidden');
        var datasets = [{
            label: 'Notes Created',
            data: noteCounts,
            backgroundColor: 'rgba(74, 144, 226, 0.7)',
            borderColor: 'rgba(74, 144, 226, 1)',
            borderWidth: 1,
            borderRadius: 4,
            yAxisID: 'y',
            order: 2
        }];
        var scales = { y: { beginAtZero: true, ticks: { stepSize: 1 }, title: { display: true, text: 'Notes' } } };
        if (hasTime) {
            datasets.push({
                label: 'Time Saved (min)',
                data: timeSaved,
                type: 'line',
                borderColor: 'rgba(75, 192, 192, 1)',
                backgroundColor: 'rgba(75, 192, 192, 0.1)',
                fill: true,
                tension: 0.3,
                yAxisID: 'y1',
                order: 1
            });
            scales.y1 = { beginAtZero: true, position: 'right', grid: { drawOnChartArea: false }, title: { display: true, text: 'Min Saved' } };
        }
        analyticsCharts.notes = new Chart(notesCtx, {
            type: 'bar',
            data: { labels, datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: hasTime } },
                scales: scales
            }
        });
    }
}

export function aggregateOfficesPeriods(offices) {
    if (!offices || offices.length === 0) return { weekly: [], monthly: [] };
    const weeklyMap = {};
    const monthlyMap = {};
    offices.forEach(office => {
        (office.weeklyNotes || []).forEach(w => {
            if (!weeklyMap[w.period]) weeklyMap[w.period] = { period: w.period, noteCount: 0, totalCost: 0, totalTimeSavedMinutes: 0 };
            weeklyMap[w.period].noteCount += w.noteCount;
            weeklyMap[w.period].totalCost += w.totalCost;
            weeklyMap[w.period].totalTimeSavedMinutes += w.totalTimeSavedMinutes;
        });
        (office.monthlyNotes || []).forEach(m => {
            if (!monthlyMap[m.period]) monthlyMap[m.period] = { period: m.period, noteCount: 0, totalCost: 0, totalTimeSavedMinutes: 0 };
            monthlyMap[m.period].noteCount += m.noteCount;
            monthlyMap[m.period].totalCost += m.totalCost;
            monthlyMap[m.period].totalTimeSavedMinutes += m.totalTimeSavedMinutes;
        });
    });
    return {
        weekly: Object.values(weeklyMap).sort((a, b) => a.period.localeCompare(b.period)),
        monthly: Object.values(monthlyMap).sort((a, b) => a.period.localeCompare(b.period))
    };
}

export function renderAnalyticsBreakdowns(data) {
    const doctorsSection = document.getElementById('analyticsDoctorsSection');
    const officesSection = document.getElementById('analyticsOfficesSection');
    const doctorsGrid = document.getElementById('analyticsDoctorsGrid');
    const officesGrid = document.getElementById('analyticsOfficesGrid');

    doctorsSection.classList.add('hidden');
    officesSection.classList.add('hidden');

    const isSuperAdmin = !data.viewingAs && analyticsViewAsHash === null && data.role === 'super_admin';
    const isViewingAs = !!data.viewingAs;

    // Per-doctor breakdown for office_admin (or viewAs mode)
    if ((data.role === 'office_admin' || isViewingAs) && data.doctors && data.doctors.length > 0) {
        doctorsSection.classList.remove('hidden');
        document.getElementById('analyticsDoctorsTitle').textContent = 'Per-Doctor Breakdown';
        doctorsGrid.innerHTML = data.doctors.map(function(doc, idx) {
            var dName = doc.displayName || '';
            if (!dName || /^[a-f0-9]{6,}$/i.test(dName)) dName = 'Profile ' + (idx + 1);
            return '<div class="user-summary-card">' +
                '<div class="user-summary-name">' + escapeHtml(dName) + '</div>' +
                '<div class="user-summary-role">' + escapeHtml(doc.role || '') + '</div>' +
                '<div class="user-summary-stats">' +
                    '<span><strong>' + (doc.noteCount || 0) + '</strong> notes</span>' +
                    '<span><strong>' + Math.round(doc.totalTimeSavedMinutes || 0) + '</strong> min saved</span>' +
                    '<span><strong>$' + (doc.costSavings || 0).toLocaleString() + '</strong> savings (est.)</span>' +
                '</div>' +
            '</div>';
        }).join('');
    }

    // Per-office breakdown for super_admin with View as Office button
    if (isSuperAdmin && data.offices && data.offices.length > 0) {
        officesSection.classList.remove('hidden');
        doctorsSection.classList.remove('hidden');
        document.getElementById('analyticsDoctorsTitle').textContent = 'Per-Office Summary';
        officesGrid.innerHTML = data.offices.map(office => `
            <div class="user-summary-card office-viewas-card" data-hash="${escapeHtml(office.emailHash)}" style="cursor:pointer">
                <div class="user-summary-name" style="cursor:pointer;text-decoration:underline">${escapeHtml(office.clinicName || 'Office ' + office.emailHash.substring(0, 6))}</div>
                <div class="user-summary-stats">
                    <span><strong>${office.doctorCount}</strong> doctors</span>
                    <span><strong>${office.totalNotes}</strong> notes</span>
                    <span><strong>${Math.round(office.totalTimeSavedMinutes || 0)}</strong> min saved</span>
                    <span><strong>$${(office.costSavings || 0).toLocaleString()}</strong> savings (est.)</span>
                </div>
                <button class="btn-small office-viewas-btn" data-hash="${escapeHtml(office.emailHash)}" style="margin-top:8px;padding:4px 12px;font-size:12px;border-radius:6px;border:1px solid #4a90e2;background:#f0f7ff;color:#4a90e2;cursor:pointer">View as Office</button>
            </div>
        `).join('');
        // Wire click handlers via event delegation (Rule 6: no inline onclick from server data)
        officesGrid.querySelectorAll('.office-viewas-card').forEach(card => {
            card.addEventListener('click', function() {
                const hash = this.dataset.hash;
                if (hash) viewAsOffice(hash);
            });
        });
    }
}

export function destroyAnalyticsCharts() {
    Object.values(analyticsCharts).forEach(chart => {
        if (chart && typeof chart.destroy === 'function') chart.destroy();
    });
    analyticsCharts = {};
}

// ============================================================================
// FEATURE FLAGS ADMIN PANEL (Super Admin only)
// ============================================================================

export async function loadFeatureFlags() {
    const ffLoading = document.getElementById('featureFlagsLoading');
    const ffContent = document.getElementById('featureFlagsContent');
    const ffError = document.getElementById('featureFlagsError');

    if (ffLoading) ffLoading.classList.remove('hidden');
    if (ffContent) ffContent.classList.add('hidden');
    if (ffError) ffError.classList.add('hidden');

    try {
        const res = await apiFetchWithRefresh('/admin/feature-flags', { method: 'GET' });
        if (!res.ok) {
            const errData = await res.json().catch(function() { return {}; });
            throw new Error(errData.error || 'Failed to load feature flags');
        }
        const data = await res.json();
        // Unwrap the {flags: {...}} envelope from the backend response (Bug #3 fix)
        analyticsFeatureFlags = data.flags || data;
        renderFeatureFlags(analyticsFeatureFlags);
        if (ffLoading) ffLoading.classList.add('hidden');
        if (ffContent) ffContent.classList.remove('hidden');
    } catch (err) {
        logger.error('ANALYTICS', 'Failed to load feature flags', err, { context: 'loadFeatureFlags' });
        if (ffLoading) ffLoading.classList.add('hidden');
        if (ffError) ffError.classList.remove('hidden');
        const errMsg = document.getElementById('featureFlagsErrorMessage');
        if (errMsg) errMsg.textContent = err.message || 'Failed to load feature flags.';
    }
}

export function renderFeatureFlags(data) {
    const flags = data.analytics || {};
    const sections = flags.sections || {};

    // Role toggles
    const officeToggle = document.getElementById('ffEnabledForOfficeAdmin');
    const doctorToggle = document.getElementById('ffEnabledForDoctor');
    if (officeToggle) officeToggle.checked = !!flags.enabledForOfficeAdmin;
    if (doctorToggle) doctorToggle.checked = !!flags.enabledForDoctor;

    // Section checkboxes
    const sectionCheckboxes = document.querySelectorAll('#ffSectionsContainer input[type="checkbox"]');
    sectionCheckboxes.forEach(function(cb) {
        const sectionKey = cb.dataset.section;
        if (sectionKey) {
            cb.checked = sections[sectionKey] !== false; // default true if not explicitly false
        }
    });

    // Quick Note flag
    const quickNoteToggle = document.getElementById('ffQuickNoteEnabled');
    if (quickNoteToggle) {
        quickNoteToggle.checked = !!(data.quickNote && data.quickNote.enabled);
    }

    // Wire up change handlers (only once, use flag to prevent duplication)
    if (!renderFeatureFlags._wired) {
        renderFeatureFlags._wired = true;
        if (officeToggle) officeToggle.addEventListener('change', handleFeatureFlagChange);
        if (doctorToggle) doctorToggle.addEventListener('change', handleFeatureFlagChange);
        sectionCheckboxes.forEach(function(cb) {
            cb.addEventListener('change', handleFeatureFlagChange);
        });
        if (quickNoteToggle) quickNoteToggle.addEventListener('change', handleFeatureFlagChange);
    }
}
renderFeatureFlags._wired = false;

export async function handleFeatureFlagChange() {
    // Build updated flags from current UI state
    const officeToggle = document.getElementById('ffEnabledForOfficeAdmin');
    const doctorToggle = document.getElementById('ffEnabledForDoctor');
    const sectionCheckboxes = document.querySelectorAll('#ffSectionsContainer input[type="checkbox"]');

    const sections = {};
    sectionCheckboxes.forEach(function(cb) {
        const key = cb.dataset.section;
        if (key) sections[key] = cb.checked;
    });

    const updatedFlags = {
        analytics: {
            enabledForOfficeAdmin: officeToggle ? officeToggle.checked : false,
            enabledForDoctor: doctorToggle ? doctorToggle.checked : false,
            sections: sections
        },
        quickNote: {
            enabled: document.getElementById('ffQuickNoteEnabled') ? document.getElementById('ffQuickNoteEnabled').checked : false
        }
    };

    // Show saving status
    const statusEl = document.getElementById('featureFlagsStatus');
    if (statusEl) {
        statusEl.textContent = 'Saving...';
        statusEl.className = 'analytics-ff-status analytics-ff-status-saving';
    }

    try {
        const res = await apiFetchWithRefresh('/admin/feature-flags', {
            method: 'PUT',
            body: JSON.stringify(updatedFlags),
            headers: { 'Content-Type': 'application/json' }
        });
        if (!res.ok) {
            const errData = await res.json().catch(function() { return {}; });
            throw new Error(errData.error || 'Failed to save feature flags');
        }
        const data = await res.json();
        // Unwrap the {flags: {...}} envelope from the backend PUT response (Bug #3 fix)
        analyticsFeatureFlags = data.flags || data;
        if (statusEl) {
            statusEl.textContent = '\u2713 Saved';
            statusEl.className = 'analytics-ff-status analytics-ff-status-success';
            setTimeout(function() { statusEl.textContent = ''; statusEl.className = 'analytics-ff-status'; }, 3000);
        }
        logger.info('ANALYTICS', 'Feature flags updated successfully');
    } catch (err) {
        logger.error('ANALYTICS', 'Failed to save feature flags', err, { context: 'handleFeatureFlagChange' });
        if (statusEl) {
            statusEl.textContent = '\u2717 Save failed: ' + (err.message || 'Unknown error');
            statusEl.className = 'analytics-ff-status analytics-ff-status-error';
        }
        // Revert UI to last known good state
        if (analyticsFeatureFlags) {
            renderFeatureFlags(analyticsFeatureFlags);
        }
    }
}

// ============================================================================
// PUBLIC FEATURE FLAGS — accessible by any authenticated user (not admin-only)
// ============================================================================

let publicFeatureFlags = null;

export async function loadPublicFlags() {
    try {
        const res = await apiFetchWithRefresh('/flags/public', { method: 'GET' });
        if (!res.ok) return;
        publicFeatureFlags = await res.json();
    } catch (e) {
        // Silent fail — flag stays null, feature hidden
    }
    updateNoteModeToggleVisibility();
    updateConsentVisibility(isConsentFormEnabled());
    updateTrialBanner();
}

export function isConsentFormEnabled() {
    return publicFeatureFlags && publicFeatureFlags.consentForm && publicFeatureFlags.consentForm.enabled === true;
}

export function isQuickNoteEnabled() {
    return publicFeatureFlags && publicFeatureFlags.quickNote && publicFeatureFlags.quickNote.enabled === true;
}

export function updateNoteModeToggleVisibility() {
    const selector = document.getElementById('noteModeSelector');
    const oldToggle = document.getElementById('noteModeToggle');
    if (oldToggle) oldToggle.style.display = 'none'; // hide old toggle forever
    if (!selector) return;
    if (isQuickNoteEnabled()) {
        selector.style.display = '';
        // Default: appointment selected
        if (!state.noteMode || state.noteMode === 'appointment') {
            selectNoteMode('appointment');
        }
    } else {
        selector.style.display = 'none';
        const banner = document.getElementById('noteModeBanner');
        if (banner) banner.style.display = 'none';
    }
}

function selectNoteMode(mode) {
    state.noteMode = mode;
    const selector = document.getElementById('noteModeSelector');
    const banner = document.getElementById('noteModeBanner');
    const bannerIcon = document.getElementById('modeBannerIcon');
    const bannerText = document.getElementById('modeBannerText');

    // Hide selector, show banner
    if (selector) selector.style.display = 'none';
    if (banner) {
        banner.style.display = '';
        banner.className = 'note-mode-banner ' + (mode === 'quick_note' ? 'mode-quicknote' : 'mode-appointment');
    }
    if (mode === 'quick_note') {
        if (bannerIcon) bannerIcon.textContent = '\u{1F4DD}';
        if (bannerText) bannerText.textContent = 'Quick Note \u2014 Doctor dictation only (no patient voice)';
    } else {
        if (bannerIcon) bannerIcon.textContent = '\u{1F399}\uFE0F';
        if (bannerText) bannerText.textContent = 'Appointment \u2014 Recording patient + doctor conversation';
    }
}

function showModeSelector() {
    const selector = document.getElementById('noteModeSelector');
    const banner = document.getElementById('noteModeBanner');
    if (selector) selector.style.display = '';
    if (banner) banner.style.display = 'none';
}

export function initNoteModeToggle() {
    // Card click handlers
    const cardAppointment = document.getElementById('cardModeAppointment');
    const cardQuickNote = document.getElementById('cardModeQuickNote');
    const changeLink = document.getElementById('modeBannerChange');

    if (cardAppointment) {
        cardAppointment.addEventListener('click', function() { selectNoteMode('appointment'); });
        cardAppointment.addEventListener('keydown', function(e) { if (e.key === 'Enter') selectNoteMode('appointment'); });
    }
    if (cardQuickNote) {
        cardQuickNote.addEventListener('click', function() { selectNoteMode('quick_note'); });
        cardQuickNote.addEventListener('keydown', function(e) { if (e.key === 'Enter') selectNoteMode('quick_note'); });
    }
    if (changeLink) {
        changeLink.addEventListener('click', function(e) { e.preventDefault(); showModeSelector(); });
    }
}

// ============================================================================
// APPOINTMENT TYPE SELECTOR â€” Record Page (Clean dropdown only)
// ============================================================================

let selectedAppointmentType = 'other_general';

// --- Recent types persistence (per profileId in localStorage, used by Note Preferences) ---
export function getRecentTypesKey() {
    var pid = currentProfileId || '_default';
    return 'dentscribe_recent_appt_types_' + pid;
}

export function getRecentTypes() {
    try {
        var stored = localStorage.getItem(getRecentTypesKey());
        if (!stored) return [];
        var arr = JSON.parse(stored);
        if (!Array.isArray(arr)) return [];
        // Validate each key exists in state.APPOINTMENT_TYPES
        return arr.filter(function(k) {
            return state.APPOINTMENT_TYPES.find(function(t) { return t.key === k; });
        }).slice(0, MAX_RECENT_TYPES);
    } catch (e) { return []; }
}

export function addToRecentTypes(typeKey) {
    if (!typeKey || typeKey === 'other_general') return;
    var recent = getRecentTypes();
    // Move to front if already present, otherwise prepend
    recent = recent.filter(function(k) { return k !== typeKey; });
    recent.unshift(typeKey);
    if (recent.length > MAX_RECENT_TYPES) recent = recent.slice(0, MAX_RECENT_TYPES);
    try {
        localStorage.setItem(getRecentTypesKey(), JSON.stringify(recent));
    } catch (e) { /* storage full â€” non-critical */ }
}

// --- Sync dropdown value ---
export function syncDropdownValue() {
    var dropdown = document.getElementById('apptTypeDropdown');
    if (dropdown) dropdown.value = selectedAppointmentType;
    var uploadDropdown = document.getElementById('uploadApptTypeDropdown');
    if (uploadDropdown) uploadDropdown.value = selectedAppointmentType;
}

export function initAppointmentTypeSelector() {
    // Restore from sessionStorage if available
    const stored = sessionStorage.getItem('dentscribe_appt_type');
    if (stored && state.APPOINTMENT_TYPES.find(function(t) { return t.key === stored; })) {
        selectedAppointmentType = stored;
    }

    // Wire categorized dropdown (Record page)
    var dropdown = document.getElementById('apptTypeDropdown');
    if (dropdown) {
        dropdown.addEventListener('change', function() {
            if (dropdown.value) selectAppointmentType(dropdown.value);
        });
    }

    // Wire upload page dropdown (same shared state)
    var uploadDropdown = document.getElementById('uploadApptTypeDropdown');
    if (uploadDropdown) {
        uploadDropdown.addEventListener('change', function() {
            if (uploadDropdown.value) selectAppointmentType(uploadDropdown.value);
        });
    }

    // Render initial state
    syncDropdownValue();

    // Load appointment types from API only if already authenticated
    // (otherwise proceedToApp() will trigger it after auth)
    if (getSessionToken()) {
        loadAppointmentTypesFromAPI();
    }
}

/**
 * Fetch appointment types from GET /appointment-types and repopulate both dropdowns.
 * Falls back silently to the hardcoded state.APPOINTMENT_TYPES if the API is unreachable.
 */
export async function loadAppointmentTypesFromAPI() {
    try {
        const res = await apiFetch('/appointment-types');
        if (!res.ok) return; // keep hardcoded fallback
        const data = await res.json();
        if (!data.appointmentTypes || !Array.isArray(data.appointmentTypes)) return;
        state.APPOINTMENT_TYPES = data.appointmentTypes;
        populateAppointmentDropdowns();
        logger.info('INIT', 'Appointment types loaded from API', { count: state.APPOINTMENT_TYPES.length });
    } catch (e) {
        // Silent fallback â€” hardcoded types remain in place
        logger.warn('INIT', 'Failed to load appointment types from API, using defaults', { error: e.message });
    }
}

/**
 * Rebuild both appointment type dropdowns from the current state.APPOINTMENT_TYPES array.
 * Groups options by category. Preserves current selection.
 */
export function populateAppointmentDropdowns() {
    var dropdownIds = ['apptTypeDropdown', 'uploadApptTypeDropdown'];
    // Group types by category
    var groups = {};
    state.APPOINTMENT_TYPES.forEach(function(t) {
        var cat = t.category || 'General';
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push(t);
    });
    // Preferred category order
    var categoryOrder = ['Exams', 'Procedures', 'Specialty', 'General'];
    var sortedCategories = categoryOrder.filter(function(c) { return groups[c]; });
    // Add any categories not in the preferred order
    Object.keys(groups).forEach(function(c) {
        if (sortedCategories.indexOf(c) === -1) sortedCategories.push(c);
    });

    dropdownIds.forEach(function(id) {
        var dropdown = document.getElementById(id);
        if (!dropdown) return;
        dropdown.innerHTML = '';
        sortedCategories.forEach(function(cat) {
            var optgroup = document.createElement('optgroup');
            optgroup.label = cat;
            groups[cat].forEach(function(t) {
                var option = document.createElement('option');
                option.value = t.key;
                option.textContent = t.label;
                if (t.key === selectedAppointmentType) option.selected = true;
                optgroup.appendChild(option);
            });
            dropdown.appendChild(optgroup);
        });
    });
}

export function selectAppointmentType(typeKey) {
    if (!typeKey || !state.APPOINTMENT_TYPES.find(function(t) { return t.key === typeKey; })) return;
    selectedAppointmentType = typeKey;
    sessionStorage.setItem('dentscribe_appt_type', typeKey);
    addToRecentTypes(typeKey);
    syncDropdownValue();
    logger.info('RECORDING', 'Appointment type selected', { type: typeKey });
}

export function updateAppointmentPillUI() {
    // Legacy compat â€” no-op now that pills are removed
}

export function getSelectedAppointmentType() {
    return selectedAppointmentType;
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', initAppointmentTypeSelector);

// ============================================================================
// NOTE FEEDBACK â€” Thumbs up/down rating in note modal
// ============================================================================

let currentFeedbackNoteId = null;
let currentFeedbackRating = null; // 'thumbs_up', 'thumbs_down', or null
let currentFeedbackText = ''; // stored feedback text

export function initNoteFeedback(noteId, existingRating, existingText) {
    currentFeedbackNoteId = noteId;
    currentFeedbackRating = existingRating || null;
    currentFeedbackText = existingText || '';

    var section = document.getElementById('noteFeedbackSection');
    var btnUp = document.getElementById('feedbackBtnUp');
    var btnDown = document.getElementById('feedbackBtnDown');
    var status = document.getElementById('noteFeedbackStatus');

    if (!section || !btnUp || !btnDown) return;

    section.classList.remove('hidden');
    if (status) { status.classList.add('hidden'); status.textContent = ''; }

    // Remove old listeners by cloning
    var newBtnUp = btnUp.cloneNode(true);
    var newBtnDown = btnDown.cloneNode(true);
    btnUp.parentNode.replaceChild(newBtnUp, btnUp);
    btnDown.parentNode.replaceChild(newBtnDown, btnDown);

    // Set initial visual state
    updateFeedbackButtonState();

    // Wire new listeners
    newBtnUp.addEventListener('click', function() {
        handleFeedbackClick('thumbs_up');
    });
    newBtnDown.addEventListener('click', function() {
        handleFeedbackClick('thumbs_down');
    });

    // Wire feedback text area
    initFeedbackTextarea();
}

export function updateFeedbackButtonState() {
    var btnUp = document.getElementById('feedbackBtnUp');
    var btnDown = document.getElementById('feedbackBtnDown');
    if (!btnUp || !btnDown) return;

    // Reset all states
    btnUp.classList.remove('active', 'dimmed');
    btnDown.classList.remove('active', 'dimmed');

    if (currentFeedbackRating === 'thumbs_up') {
        btnUp.classList.add('active');
        btnDown.classList.add('dimmed');
    } else if (currentFeedbackRating === 'thumbs_down') {
        btnDown.classList.add('active');
        btnUp.classList.add('dimmed');
    }
}

export async function handleFeedbackClick(rating) {
    if (!currentFeedbackNoteId) return;

    var btnUp = document.getElementById('feedbackBtnUp');
    var btnDown = document.getElementById('feedbackBtnDown');
    var status = document.getElementById('noteFeedbackStatus');

    // Toggle: clicking the active rating clears it
    var newRating = (currentFeedbackRating === rating) ? null : rating;

    // Disable buttons during save
    if (btnUp) btnUp.disabled = true;
    if (btnDown) btnDown.disabled = true;
    if (status) {
        status.textContent = 'Saving...';
        status.className = 'feedback-status feedback-status-saving';
        status.classList.remove('hidden');
    }

    try {
        var res = await apiFetchWithRefresh('/notes/' + currentFeedbackNoteId, {
            method: 'PATCH',
            body: JSON.stringify({ feedbackRating: newRating }),
        });

        if (!res.ok) {
            var errData = await res.json().catch(function() { return {}; });
            throw new Error(errData.error || 'Failed to save feedback');
        }

        currentFeedbackRating = newRating;
        updateFeedbackButtonState();

        if (status) {
            status.textContent = newRating ? 'Saved!' : 'Rating cleared';
            status.className = 'feedback-status feedback-status-success';
            setTimeout(function() { status.classList.add('hidden'); }, 2000);
        }
        logger.info('NOTES', 'Feedback submitted', { noteId: currentFeedbackNoteId, rating: newRating || 'cleared' });
    } catch (err) {
        if (status) {
            status.textContent = 'Failed to save. Tap to retry.';
            status.className = 'feedback-status feedback-status-error';
        }
        logger.error('NOTES', 'Feedback save failed', err, { noteId: currentFeedbackNoteId });
    } finally {
        if (btnUp) btnUp.disabled = false;
        if (btnDown) btnDown.disabled = false;
    }
}

function initFeedbackTextarea() {
    var textarea = document.getElementById('feedbackTextarea');
    var saveBtn = document.getElementById('feedbackTextSaveBtn');
    var savedMsg = document.getElementById('feedbackTextSavedMsg');
    if (!textarea || !saveBtn) return;

    // Populate existing text
    textarea.value = currentFeedbackText || '';
    saveBtn.classList.add('d-none');
    if (savedMsg) savedMsg.classList.add('d-none');

    // Show save button when text differs from saved value
    textarea.addEventListener('input', function() {
        var hasChange = textarea.value.trim() !== (currentFeedbackText || '');
        if (hasChange) {
            saveBtn.classList.remove('d-none');
        } else {
            saveBtn.classList.add('d-none');
        }
        if (savedMsg) savedMsg.classList.add('d-none');
    });

    // Save on click
    saveBtn.addEventListener('click', function() {
        saveFeedbackText();
    });
}

async function saveFeedbackText() {
    if (!currentFeedbackNoteId) return;
    var textarea = document.getElementById('feedbackTextarea');
    var saveBtn = document.getElementById('feedbackTextSaveBtn');
    var savedMsg = document.getElementById('feedbackTextSavedMsg');
    if (!textarea) return;

    var text = textarea.value.trim();
    var payload = { feedbackText: text || null };

    if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = 'Saving...'; }

    try {
        var res = await apiFetchWithRefresh('/notes/' + currentFeedbackNoteId, {
            method: 'PATCH',
            body: JSON.stringify(payload),
        });
        if (!res.ok) {
            var errData = await res.json().catch(function() { return {}; });
            throw new Error(errData.error || 'Failed to save comment');
        }
        currentFeedbackText = text || '';
        if (saveBtn) { saveBtn.classList.add('d-none'); saveBtn.disabled = false; saveBtn.textContent = 'Save'; }
        if (savedMsg) {
            savedMsg.classList.remove('d-none');
            setTimeout(function() { savedMsg.classList.add('d-none'); }, 2000);
        }
        logger.info('NOTES', 'Feedback text saved', { noteId: currentFeedbackNoteId });
    } catch (err) {
        if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Save'; }
        showToast('Failed to save comment: ' + err.message, 'error');
        logger.error('NOTES', 'Feedback text save failed', err, { noteId: currentFeedbackNoteId });
    }
}

export function resetNoteFeedback() {
    currentFeedbackNoteId = null;
    currentFeedbackRating = null;
    currentFeedbackText = '';
    var section = document.getElementById('noteFeedbackSection');
    if (section) section.classList.add('hidden');
    var btnUp = document.getElementById('feedbackBtnUp');
    var btnDown = document.getElementById('feedbackBtnDown');
    if (btnUp) btnUp.classList.remove('active', 'dimmed');
    if (btnDown) btnDown.classList.remove('active', 'dimmed');
    var status = document.getElementById('noteFeedbackStatus');
    if (status) { status.classList.add('hidden'); status.textContent = ''; }
    // Reset textarea
    var textarea = document.getElementById('feedbackTextarea');
    if (textarea) textarea.value = '';
    var saveBtn = document.getElementById('feedbackTextSaveBtn');
    if (saveBtn) saveBtn.classList.add('d-none');
    var savedMsg = document.getElementById('feedbackTextSavedMsg');
    if (savedMsg) savedMsg.classList.add('d-none');
}

// ============================================================================
// MOCK DATA GENERATOR â€” For frontend development without backend
// ============================================================================

export function generateMockAnalyticsData() {
    var now = new Date();
    var todayStr = now.toISOString().split('T')[0];

    // Generate weekly periods (last 8 weeks)
    var weeklyNotes = [];
    for (var w = 7; w >= 0; w--) {
        var weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - (w * 7));
        var period = weekStart.toISOString().split('T')[0];
        var noteCount = Math.floor(Math.random() * 12) + 3;
        weeklyNotes.push({
            period: period,
            noteCount: noteCount,
            totalCost: +(noteCount * 0.073).toFixed(2),
            totalTimeSavedMinutes: noteCount * 9,
            audioDurationSeconds: noteCount * 180
        });
    }

    // Generate monthly periods (last 6 months)
    var monthlyNotes = [];
    for (var m = 5; m >= 0; m--) {
        var monthDate = new Date(now.getFullYear(), now.getMonth() - m, 1);
        var period = monthDate.toISOString().substring(0, 7);
        var noteCount = Math.floor(Math.random() * 40) + 15;
        monthlyNotes.push({
            period: period,
            noteCount: noteCount,
            totalCost: +(noteCount * 0.073).toFixed(2),
            totalTimeSavedMinutes: noteCount * 9,
            audioDurationSeconds: noteCount * 180
        });
    }

    // Appointment types breakdown
    var appointmentTypes = {
        hygiene_recall: 18,
        new_patient_exam: 12,
        filling_restoration: 10,
        other_general: 9,
        crown_bridge: 7,
        periodontal_visit: 6,
        emergency_visit: 5,
        root_canal: 4,
        extraction: 3,
        implant_consult: 2,
        ortho_consult: 2,
        denture_prosthodontic: 1
    };

    // Peak usage hours (count per hour, 0-23)
    var peakHours = [];
    for (var h = 0; h < 24; h++) {
        var count = 0;
        if (h >= 8 && h <= 12) count = Math.floor(Math.random() * 8) + 4;
        else if (h >= 13 && h <= 17) count = Math.floor(Math.random() * 6) + 3;
        else if (h >= 7 && h <= 18) count = Math.floor(Math.random() * 3) + 1;
        peakHours.push({ hour: h, count: count });
    }

    // Provider leaderboard (mock doctors)
    var doctors = [
        { profileId: 'mock-uuid-1', displayName: 'Dr. Anderson', noteCount: 28, totalTimeSavedMinutes: 252, costSavings: 630, avgGenTime: 12.4, role: 'doctor' },
        { profileId: 'mock-uuid-2', displayName: 'Dr. Martinez', noteCount: 22, totalTimeSavedMinutes: 198, costSavings: 495, avgGenTime: 14.1, role: 'doctor' },
        { profileId: 'mock-uuid-3', displayName: 'Dr. Chen', noteCount: 18, totalTimeSavedMinutes: 162, costSavings: 405, avgGenTime: 11.8, role: 'doctor' },
        { profileId: 'mock-uuid-4', displayName: 'Dr. Williams', noteCount: 11, totalTimeSavedMinutes: 99, costSavings: 247.5, avgGenTime: 15.2, role: 'doctor' }
    ];

    // Appointment type performance
    var apptPerformance = [
        { type: 'hygiene_recall', noteCount: 18, avgGenTime: 11.2, avgTimeSaved: 8.5, successRate: 98 },
        { type: 'new_patient_exam', noteCount: 12, avgGenTime: 16.8, avgTimeSaved: 12.1, successRate: 95 },
        { type: 'filling_restoration', noteCount: 10, avgGenTime: 10.5, avgTimeSaved: 7.8, successRate: 99 },
        { type: 'other_general', noteCount: 9, avgGenTime: 13.2, avgTimeSaved: 9.0, successRate: 97 },
        { type: 'crown_bridge', noteCount: 7, avgGenTime: 15.4, avgTimeSaved: 11.3, successRate: 96 },
        { type: 'periodontal_visit', noteCount: 6, avgGenTime: 14.0, avgTimeSaved: 10.2, successRate: 94 },
        { type: 'emergency_visit', noteCount: 5, avgGenTime: 9.8, avgTimeSaved: 7.2, successRate: 100 },
        { type: 'root_canal', noteCount: 4, avgGenTime: 18.1, avgTimeSaved: 13.5, successRate: 92 }
    ];

    // Notes needing review: empty in mock mode. The real notesNeedingReview comes from
    // the backend (lambda/routes/analytics.py _aggregate_notes_needing_review) and flows
    // through unchanged when ANALYTICS_USE_MOCK is false (production).
    var notesNeedingReview = [];

    // Daily heatmap data (last 12 weeks, 84 days)
    var heatmapData = [];
    for (var d = 83; d >= 0; d--) {
        var day = new Date(now);
        day.setDate(day.getDate() - d);
        var dayOfWeek = day.getDay();
        var count = 0;
        // Weekdays have more activity
        if (dayOfWeek >= 1 && dayOfWeek <= 5) {
            count = Math.floor(Math.random() * 6) + (Math.random() > 0.2 ? 1 : 0);
        } else {
            count = Math.random() > 0.7 ? Math.floor(Math.random() * 2) + 1 : 0;
        }
        heatmapData.push({ date: day.toISOString().split('T')[0], count: count });
    }

    // Today's stats
    var notesToday = Math.floor(Math.random() * 5) + 2;
    var avgGenTimeSeconds = 12.5 + (Math.random() * 5);
    var timeSavedToday = notesToday * 9;
    var activeProviders = Math.floor(Math.random() * 3) + 2;

    // ROI data
    var totalNotes = 79;
    var hourlyRate = 50;
    var totalTimeSavedMinutes = 711;
    var roi = {
        totalTimeSavedHours: +(totalTimeSavedMinutes / 60).toFixed(1),
        dollarValueSaved: Math.round((totalTimeSavedMinutes / 60) * hourlyRate),
        awsCost: 5.77,
        netSavings: Math.round((totalTimeSavedMinutes / 60) * hourlyRate) - 5.77,
        roiMultiplier: Math.round(((totalTimeSavedMinutes / 60) * hourlyRate) / 5.77),
        costPerNote: +(5.77 / totalNotes).toFixed(3)
    };

    return {
        role: 'office_admin',
        officeTotals: {
            totalNotes: totalNotes,
            totalTimeSavedMinutes: totalTimeSavedMinutes,
            totalAudioDurationSeconds: totalNotes * 180,
            costSavings: Math.round((totalTimeSavedMinutes / 60) * hourlyRate),
            avgTimeSavedPerNote: +(totalTimeSavedMinutes / totalNotes).toFixed(1),
            totalCost: roi.awsCost,
            totalTranscribeCost: 3.27,
            totalBedrockCost: 2.50
        },
        weeklyNotes: weeklyNotes,
        monthlyNotes: monthlyNotes,
        doctors: doctors,
        appointmentTypes: appointmentTypes,
        peakHours: peakHours,
        apptPerformance: apptPerformance,
        notesNeedingReview: notesNeedingReview,
        heatmapData: heatmapData,
        todaySummary: {
            notesToday: notesToday,
            avgGenTimeSeconds: +avgGenTimeSeconds.toFixed(1),
            timeSavedToday: timeSavedToday,
            activeProviders: activeProviders
        },
        roi: roi,
        visibleSections: {
            noteCount: true,
            timeSaved: true,
            costSavings: true,
            recordingActivityChart: true,
            timeSavedChart: true,
            perDoctorBreakdown: true,
            appointmentTypes: true,
            peakHours: true,
            providerLeaderboard: true,
            apptPerformance: true,
            notesReview: true,
            roiSummary: true,
            streakHeatmap: true
        }
    };
}

// ============================================================================
// ENHANCED ANALYTICS RENDERERS â€” New dashboard sections
// ============================================================================

export function renderEnhancedAnalytics(data) {
    // Cache for date-range re-rendering
    window._lastAnalyticsData = data;
    // Apply date-range filter for chart/section rendering
    var filtered = filterDataByDateRange(data);
    renderTodaySummary(data);
    renderAppointmentTypeChart(filtered);
    renderPeakUsageHours(filtered);
    renderProviderLeaderboard(filtered);
    renderApptPerformanceTable(filtered);
    renderNotesNeedingReview(filtered);
    renderRoiSummary(data);
    renderActivityHeatmap(filtered);
    renderNeedsReviewKPI(data);
}

// --- Today's Summary ---
export function renderTodaySummary(data) {
    var section = document.getElementById('analyticsTodaySummary');
    if (!section) return;

    var today = data.todaySummary;
    var dateEl = document.getElementById('analyticsTodayDate');
    var notesTodayEl = document.getElementById('analyticsNotesToday');
    var avgGenEl = document.getElementById('analyticsAvgGenTime');
    var timeSavedEl = document.getElementById('analyticsTimeSavedToday');
    var providersEl = document.getElementById('analyticsActiveProviders');

    if (dateEl) {
        var now = new Date();
        dateEl.textContent = now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }

    if (today) {
        if (notesTodayEl) notesTodayEl.textContent = today.notesToday || 0;
        if (avgGenEl) avgGenEl.textContent = today.avgGenTimeSeconds ? today.avgGenTimeSeconds.toFixed(1) + 's' : '--';
        if (timeSavedEl) timeSavedEl.textContent = (today.timeSavedToday || 0) + ' min';
        if (providersEl) providersEl.textContent = today.activeProviders || 0;
    } else {
        // Derive from existing data if todaySummary not present
        var todayNotes = deriveTodayNotes(data);
        if (notesTodayEl) notesTodayEl.textContent = todayNotes;
        if (avgGenEl) avgGenEl.textContent = '--';
        if (timeSavedEl) timeSavedEl.textContent = (todayNotes * 9) + ' min';
        if (providersEl) providersEl.textContent = data.doctors ? data.doctors.length : 1;
    }
}

export function deriveTodayNotes(data) {
    // Try to derive today's note count from weekly data
    var weekly = data.weeklyNotes || [];
    if (weekly.length > 0) {
        var lastWeek = weekly[weekly.length - 1];
        // Approximate: divide weekly by days elapsed in week
        var weekStart = new Date(lastWeek.period);
        var today = new Date();
        var daysInWeek = Math.max(1, Math.ceil((today - weekStart) / 86400000));
        return Math.round((lastWeek.noteCount || 0) / daysInWeek);
    }
    return 0;
}

// --- Notes by Appointment Type (Horizontal Bar Chart) ---
export function renderAppointmentTypeChart(data) {
    var section = document.getElementById('analyticsApptTypeSection');
    var canvas = document.getElementById('chartApptType');
    if (!section || !canvas) return;

    var apptData = data.appointmentTypes;
    if (!apptData || Object.keys(apptData).length === 0) {
        section.classList.add('hidden');
        return;
    }

    section.classList.remove('hidden');

    // Sort by count descending
    var sorted = Object.entries(apptData).sort(function(a, b) { return b[1] - a[1]; });
    var labels = sorted.map(function(entry) {
        var type = state.APPOINTMENT_TYPES.find(function(t) { return t.key === entry[0]; });
        return type ? type.shortLabel : entry[0];
    });
    var counts = sorted.map(function(entry) { return entry[1]; });

    // Color palette for appointment types
    var colors = [
        '#2563EB', '#0891B2', '#7C3AED', '#DC2626', '#EA580C',
        '#CA8A04', '#16A34A', '#9333EA', '#0D9488', '#DB2777',
        '#4F46E5', '#78716C'
    ];

    // Destroy previous instance if exists
    if (analyticsCharts.apptType) {
        analyticsCharts.apptType.destroy();
    }

    analyticsCharts.apptType = new Chart(canvas, {
        type: 'bar',
        data: {
            labels: labels,
            datasets: [{
                label: 'Notes',
                data: counts,
                backgroundColor: colors.slice(0, counts.length),
                borderWidth: 0,
                borderRadius: 4
            }]
        },
        options: {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            onClick: function(event, elements) {
                if (elements && elements.length > 0) {
                    var idx = elements[0].index;
                    var typeKey = sorted[idx][0];
                    highlightApptPerformanceRow(typeKey);
                }
            },
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: function(ctx) {
                            var total = counts.reduce(function(a, b) { return a + b; }, 0);
                            var pct = total > 0 ? Math.round((ctx.parsed.x / total) * 100) : 0;
                            return ctx.parsed.x + ' notes (' + pct + '%)';
                        }
                    }
                }
            },
            scales: {
                x: { beginAtZero: true, ticks: { stepSize: 1 } },
                y: { ticks: { font: { size: 12 } } }
            }
        }
    });
}

// --- Peak Usage Hours ---
export function renderPeakUsageHours(data) {
    var container = document.getElementById('analyticsPeakHoursGrid');
    var section = document.getElementById('analyticsPeakHoursSection');
    if (!container || !section) return;

    var peakData = data.peakHours;
    if (!peakData || peakData.length === 0) {
        section.classList.add('hidden');
        return;
    }

    section.classList.remove('hidden');

    // Convert UTC hours to local timezone
    var tzOffset = new Date().getTimezoneOffset() / -60;
    var localPeakData = peakData.map(function(item) {
        var localHour = (item.hour + tzOffset) % 24;
        if (localHour < 0) localHour += 24;
        return { hour: localHour, count: item.count };
    }).sort(function(a, b) { return a.hour - b.hour; });

    var maxCount = Math.max.apply(null, localPeakData.map(function(h) { return h.count; }));
    if (maxCount === 0) maxCount = 1;

    var html = '<div class="peak-hours-bars">';
    localPeakData.forEach(function(item) {
        var pct = Math.round((item.count / maxCount) * 100);
        var intensity = item.count === 0 ? 0 : Math.max(1, Math.ceil((item.count / maxCount) * 4));
        var hourLabel = item.hour === 0 ? '12a' : item.hour < 12 ? item.hour + 'a' : item.hour === 12 ? '12p' : (item.hour - 12) + 'p';
        html += '<div class="peak-hour-col">';
        html += '<div class="peak-hour-bar" style="height:' + pct + '%" data-level="' + intensity + '" title="' + hourLabel + ': ' + item.count + ' notes"></div>';
        html += '<span class="peak-hour-label">' + hourLabel + '</span>';
        html += '</div>';
    });
    html += '</div>';
    container.innerHTML = html;
}

// --- Provider Leaderboard ---
export function renderProviderLeaderboard(data) {
    var section = document.getElementById('analyticsLeaderboardSection');
    var table = document.getElementById('analyticsLeaderboardTable');
    if (!section || !table) return;

    var doctors = data.doctors;
    if (!doctors || doctors.length === 0) {
        section.classList.add('hidden');
        return;
    }

    section.classList.remove('hidden');

    // Sort by noteCount descending
    var sorted = doctors.slice().sort(function(a, b) { return (b.noteCount || 0) - (a.noteCount || 0); });

    var html = '<div class="leaderboard-header">';
    html += '<span class="lb-rank">#</span>';
    html += '<span class="lb-name">Provider</span>';
    html += '<span class="lb-notes">Notes</span>';
    html += '<span class="lb-time">Time Saved</span>';
    html += '<span class="lb-gen">Avg Gen</span>';
    html += '</div>';

    sorted.forEach(function(doc, index) {
        var medal = index === 0 ? '\uD83E\uDD47' : index === 1 ? '\uD83E\uDD48' : index === 2 ? '\uD83E\uDD49' : String(index + 1);
        var name = doc.displayName || '';
        if (!name || /^[a-f0-9]{6,}$/i.test(name)) name = 'Profile ' + (index + 1);
        html += '<div class="leaderboard-row" data-profile-id="' + escapeHtml(doc.profileId) + '">';
        html += '<span class="lb-rank">' + medal + '</span>';
        html += '<span class="lb-name">' + escapeHtml(name) + '</span>';
        html += '<span class="lb-notes">' + (doc.noteCount || 0) + '</span>';
        html += '<span class="lb-time">' + Math.round(doc.totalTimeSavedMinutes || 0) + ' min</span>';
        html += '<span class="lb-gen">' + (doc.avgGenTime ? doc.avgGenTime.toFixed(1) + 's' : '--') + '</span>';
        html += '</div>';
    });

    table.innerHTML = html;

    // Wire click handler for provider drill-down
    table.querySelectorAll('.leaderboard-row').forEach(function(row) {
        row.addEventListener('click', function() {
            var pid = this.dataset.profileId;
            if (pid) {
                logger.info('ANALYTICS', 'Provider leaderboard row clicked', { context: 'leaderboard' });
                // If analytics profile select exists, switch to that profile
                var profileSelect = document.getElementById('analyticsProfileSelect');
                if (profileSelect && !profileSelect.classList.contains('hidden')) {
                    profileSelect.value = pid;
                    onAnalyticsProfileChange();
                }
            }
        });
    });
}

// --- Appointment Type Performance Table ---
export function renderApptPerformanceTable(data) {
    var section = document.getElementById('analyticsApptPerformanceSection');
    var table = document.getElementById('analyticsApptPerformanceTable');
    if (!section || !table) return;

    var perfData = data.apptPerformance;
    if (!perfData || perfData.length === 0) {
        // Fall back to appointment type counts if no performance data
        if (data.appointmentTypes && Object.keys(data.appointmentTypes).length > 0) {
            perfData = Object.entries(data.appointmentTypes).map(function(entry) {
                return { type: entry[0], noteCount: entry[1], avgGenTime: null, avgTimeSaved: null, successRate: null };
            });
        } else {
            section.classList.add('hidden');
            return;
        }
    }

    section.classList.remove('hidden');

    var html = '<div class="appt-perf-header">';
    html += '<span class="ap-type">Type</span>';
    html += '<span class="ap-count">Notes</span>';
    html += '<span class="ap-gen">Avg Gen</span>';
    html += '<span class="ap-saved">Avg Saved</span>';
    html += '<span class="ap-rate">Success</span>';
    html += '</div>';

    perfData.forEach(function(item) {
        var typeInfo = state.APPOINTMENT_TYPES.find(function(t) { return t.key === item.type; });
        var label = typeInfo ? typeInfo.shortLabel : item.type;
        var rateClass = item.successRate >= 97 ? 'rate-excellent' : item.successRate >= 94 ? 'rate-good' : 'rate-fair';

        html += '<div class="appt-perf-row" data-type="' + escapeHtml(item.type) + '">';
        html += '<span class="ap-type">' + escapeHtml(label) + '</span>';
        html += '<span class="ap-count">' + (item.noteCount || 0) + '</span>';
        html += '<span class="ap-gen">' + (item.avgGenTime ? item.avgGenTime.toFixed(1) + 's' : '--') + '</span>';
        html += '<span class="ap-saved">' + (item.avgTimeSaved ? item.avgTimeSaved.toFixed(1) + ' min' : '--') + '</span>';
        html += '<span class="ap-rate ' + rateClass + '">' + (item.successRate ? item.successRate + '%' : '--') + '</span>';
        html += '</div>';
    });

    table.innerHTML = html;
}

// --- Notes Needing Review ---
export function renderNotesNeedingReview(data) {
    var section = document.getElementById('analyticsReviewSection');
    var list = document.getElementById('analyticsReviewList');
    if (!section || !list) return;

    var notes = data.notesNeedingReview;
    if (!notes || notes.length === 0) {
        list.innerHTML = '<div class="analytics-review-empty">All notes are up to date. \u2713</div>';
        section.classList.remove('hidden');
        return;
    }

    section.classList.remove('hidden');

    var html = '';
    notes.forEach(function(note) {
        var typeInfo = state.APPOINTMENT_TYPES.find(function(t) { return t.key === note.appointmentType; });
        var typeLabel = typeInfo ? typeInfo.shortLabel : (note.appointmentType || 'General');
        var timeAgo = getTimeAgo(note.createdAt);

        html += '<div class="review-item" data-note-id="' + escapeHtml(note.noteId) + '">';
        html += '<div class="review-item-header">';
        html += '<span class="review-type-badge">' + escapeHtml(typeLabel) + '</span>';
        html += '<span class="review-time">' + escapeHtml(timeAgo) + '</span>';
        html += '</div>';
        html += '<div class="review-reason">' + escapeHtml(note.reason) + '</div>';
        html += '</div>';
    });

    list.innerHTML = html;

    // Wire click handlers to open notes
    list.querySelectorAll('.review-item').forEach(function(item) {
        item.addEventListener('click', function() {
            var noteId = this.dataset.noteId;
            if (noteId && noteId.indexOf('mock') === -1) {
                hideAnalytics();
                setTimeout(function() { viewNote(noteId); }, 300);
            }
        });
        item.style.cursor = 'pointer';
    });
}

export function getTimeAgo(isoStr) {
    if (!isoStr) return '';
    var diff = Date.now() - new Date(isoStr).getTime();
    var minutes = Math.floor(diff / 60000);
    if (minutes < 60) return minutes + ' min ago';
    var hours = Math.floor(minutes / 60);
    if (hours < 24) return hours + 'h ago';
    var days = Math.floor(hours / 24);
    return days + 'd ago';
}

// --- ROI Summary ---
export function renderRoiSummary(data) {
    var section = document.getElementById('analyticsRoiSection');
    var grid = document.getElementById('analyticsRoiGrid');
    if (!section || !grid) return;

    var roi = data.roi;
    if (!roi) {
        // Derive basic ROI from existing data
        var totals = data.officeTotals || data.stats || {};
        var timeSaved = totals.totalTimeSavedMinutes || 0;
        var cost = totals.totalCost || 0;
        var hourlyRate = 50;
        roi = {
            totalTimeSavedHours: +(timeSaved / 60).toFixed(1),
            dollarValueSaved: Math.round((timeSaved / 60) * hourlyRate),
            roiMultiplier: cost > 0 ? Math.round(((timeSaved / 60) * hourlyRate) / cost) : 0,
        };
    }

    section.classList.remove('hidden');

    var html = '';
    html += '<div class="roi-card roi-highlight">';
    html += '<div class="roi-value">' + roi.roiMultiplier + 'x</div>';
    html += '<div class="roi-label">ROI Multiplier</div>';
    html += '</div>';
    html += '<div class="roi-card">';
    html += '<div class="roi-value">$' + (roi.dollarValueSaved || 0).toLocaleString() + '</div>';
    html += '<div class="roi-label">Value of Time Saved</div>';
    html += '</div>';
    html += '<div class="roi-card">';
    html += '<div class="roi-value">' + (roi.totalTimeSavedHours || 0) + 'h</div>';
    html += '<div class="roi-label">Hours Saved</div>';
    html += '</div>';

    grid.innerHTML = html;
}

// --- Activity Heatmap (GitHub-style) ---
export function renderActivityHeatmap(data) {
    var section = document.getElementById('analyticsHeatmapSection');
    var grid = document.getElementById('analyticsHeatmapGrid');
    if (!section || !grid) return;

    var heatmapData = data.heatmapData;
    if (!heatmapData || heatmapData.length === 0) {
        // Generate from weekly notes if heatmap data not available
        heatmapData = deriveHeatmapFromWeekly(data);
    }

    if (!heatmapData || heatmapData.length === 0) {
        section.classList.add('hidden');
        return;
    }

    section.classList.remove('hidden');

    // Find max count for level calculation
    var maxCount = Math.max.apply(null, heatmapData.map(function(d) { return d.count; }));
    if (maxCount === 0) maxCount = 1;

    // Organize into weeks (columns) x days (rows)
    var firstDate = new Date(heatmapData[0].date);
    var startDayOfWeek = firstDate.getDay(); // 0=Sun

    var html = '<div class="heatmap-days-labels">';
    var dayLabels = ['', 'Mon', '', 'Wed', '', 'Fri', ''];
    dayLabels.forEach(function(label) {
        html += '<div class="heatmap-day-label">' + label + '</div>';
    });
    html += '</div>';
    html += '<div class="heatmap-cells">';

    // Add empty cells for padding
    for (var p = 0; p < startDayOfWeek; p++) {
        html += '<div class="heatmap-cell empty"></div>';
    }

    heatmapData.forEach(function(day) {
        var level = day.count === 0 ? 0 : Math.min(4, Math.ceil((day.count / maxCount) * 4));
        html += '<div class="heatmap-cell" data-level="' + level + '" data-date="' + escapeHtml(day.date) + '" title="' + escapeHtml(day.date) + ': ' + day.count + ' notes"></div>';
    });

    html += '</div>';
    grid.innerHTML = html;
}

export function deriveHeatmapFromWeekly(data) {
    // Can't derive individual days from weekly aggregates â€” return empty
    return [];
}

// ============================================================================
// ANALYTICS DATE RANGE FILTER â€” Client-side filtering
// ============================================================================
let analyticsDateRange = '30days'; // Default: 30 days

export function setAnalyticsDateRange(range) {
    analyticsDateRange = range;
    // Update pill active state
    var pills = document.querySelectorAll('.analytics-date-pill');
    pills.forEach(function(pill) {
        pill.classList.toggle('active', pill.dataset.range === range);
    });
    // Re-render with filtered data if we have cached analytics data
    if (window._lastAnalyticsData) {
        renderAnalyticsDashboard(window._lastAnalyticsData, document.getElementById('analyticsProfileSelect') ? document.getElementById('analyticsProfileSelect').value : null);
        // Bug #4 fix: also re-render enhanced analytics (heatmap, leaderboard, etc.) with filtered data
        renderEnhancedAnalytics(window._lastAnalyticsData);
    }
    logger.info('ANALYTICS', 'Date range changed', { range: range });
}

export function filterDataByDateRange(data) {
    if (!data || analyticsDateRange === 'all') return data;

    var now = new Date();
    var cutoff;
    switch (analyticsDateRange) {
        case 'today':
            cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            break;
        case '7days':
            cutoff = new Date(now.getTime() - 7 * 86400000);
            break;
        case '30days':
            cutoff = new Date(now.getTime() - 30 * 86400000);
            break;
        case 'month':
            cutoff = new Date(now.getFullYear(), now.getMonth(), 1);
            break;
        default:
            return data;
    }

    var filtered = Object.assign({}, data);

    // Filter weeklyNotes by period — include weeks that overlap the cutoff date
    // (a week's period is its start date; it spans 7 days forward)
    if (filtered.weeklyNotes) {
        filtered.weeklyNotes = filtered.weeklyNotes.filter(function(w) {
            var weekStart = new Date(w.period);
            var weekEnd = new Date(weekStart.getTime() + 6 * 86400000);
            return weekEnd >= cutoff;
        });
    }
    // Filter monthlyNotes by period
    if (filtered.monthlyNotes) {
        filtered.monthlyNotes = filtered.monthlyNotes.filter(function(m) {
            return new Date(m.period + '-01') >= cutoff;
        });
    }
    // Filter heatmapData by date
    if (filtered.heatmapData) {
        filtered.heatmapData = filtered.heatmapData.filter(function(d) {
            return new Date(d.date) >= cutoff;
        });
    }
    // Bug #4 fix: Also filter offices' weeklyNotes for super admin aggregation
    if (filtered.offices && Array.isArray(filtered.offices)) {
        filtered.offices = filtered.offices.map(function(office) {
            var filteredOffice = Object.assign({}, office);
            if (filteredOffice.weeklyNotes) {
                filteredOffice.weeklyNotes = filteredOffice.weeklyNotes.filter(function(w) {
                    var weekStart = new Date(w.period);
                    var weekEnd = new Date(weekStart.getTime() + 6 * 86400000);
                    return weekEnd >= cutoff;
                });
            }
            if (filteredOffice.monthlyNotes) {
                filteredOffice.monthlyNotes = filteredOffice.monthlyNotes.filter(function(m) {
                    return new Date(m.period + '-01') >= cutoff;
                });
            }
            return filteredOffice;
        });
    }

    return filtered;
}

// --- Needs Review KPI renderer ---

export function renderNeedsReviewKPI(data) {
    var el = document.getElementById('analyticsNeedsReviewCount');
    if (!el) return;
    var count = (data.notesNeedingReview && data.notesNeedingReview.length) || 0;
    el.textContent = count;
    // Hide card when 0, show when > 0
    var card = document.getElementById('analyticsNeedsReviewCard');
    if (card) {
        if (count > 0) {
            card.classList.remove('hidden');
            card.classList.add('kpi-needs-attention');
        } else {
            card.classList.add('hidden');
            card.classList.remove('kpi-needs-attention');
        }
    }
}

// --- Highlight appointment performance row when chart bar is clicked ---
export function highlightApptPerformanceRow(typeKey) {
    var table = document.getElementById('analyticsApptPerformanceTable');
    if (!table) return;
    // Remove existing highlights
    table.querySelectorAll('.appt-perf-row').forEach(function(row) {
        row.classList.remove('appt-perf-highlighted');
    });
    // Find and highlight the matching row
    var target = table.querySelector('.appt-perf-row[data-type="' + typeKey + '"]');
    if (target) {
        target.classList.add('appt-perf-highlighted');
        // Scroll it into view
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    // Also scroll to the performance table section
    var section = document.getElementById('analyticsApptPerformanceSection');
    if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
}


// ============================================================================
// PROVIDER PROFILE PAGE
// ============================================================================

/**
 * Show the Provider Profile page view.
 * Sets hash for navigation persistence, renders cached data immediately,
 * then fetches analytics asynchronously.
 */
export function showProviderProfile() {
    if (!currentProfileId) {
        logger.warn('PROFILE', 'Cannot show profile â€” no active profile');
        return;
    }
    navigateTo('profile');
    location.hash = '#profile';
    renderProviderProfile();
}

/**
 * Hide the Provider Profile page and return to the main app.
 * Performs complete state reset per code-patterns.md.
 */
export function hideProviderProfile() {
    navigateTo('record');
    if (location.hash === '#profile') {
        history.replaceState(null, '', location.pathname + location.search);
    }
    // Complete state reset (per code-patterns.md)
    var prefsCard = document.getElementById('profilePrefsCard');
    var templatesCard = document.getElementById('profileTemplatesCard');
    var settingsCard = document.getElementById('profileSettingsCard');
    var avatarEl = document.getElementById('profilePageAvatar');
    var nameEl = document.getElementById('profilePageName');
    var roleEl = document.getElementById('profilePageRole');

    if (prefsCard) prefsCard.innerHTML = '';
    if (templatesCard) templatesCard.innerHTML = '';
    if (settingsCard) settingsCard.innerHTML = '';
    if (avatarEl) avatarEl.textContent = '';
    if (nameEl) nameEl.textContent = '';
    if (roleEl) roleEl.textContent = '';
}

/**
 * Render the Provider Profile page.
 * Instant render from cached data, async fetch for stats.
 */
export async function renderProviderProfile() {
    // 1. Find profile from cached data (instant, no fetch needed)
    var profile = null;
    if (profilesData && profilesData.profiles) {
        profile = profilesData.profiles.find(function(p) {
            return p.profileId === currentProfileId;
        });
    }
    if (!profile) {
        // Try fetching fresh from API before giving up
        logger.warn('PROFILE', 'Profile not found in cache, fetching fresh');
        try {
            var freshRes = await apiFetchWithRefresh('/profiles');
            if (freshRes.ok) {
                var freshData = await freshRes.json();
                profilesData = freshData;
                if (freshData && freshData.profiles) {
                    profile = freshData.profiles.find(function(p) {
                        return p.profileId === currentProfileId;
                    });
                }
            }
        } catch (fetchErr) {
            logger.error('PROFILE', 'Failed to fetch profiles from API', fetchErr);
        }
    }
    if (!profile) {
        // Still not found after fresh fetch â€” show error on profile page, don't navigate away
        logger.warn('PROFILE', 'Profile not found after fresh fetch');
        renderProfilePageError('Profile not found. It may have been deleted or you may not have access.');
        return;
    }

    // 2. Render cover card immediately from cached profile
    renderProfileCover(profile);

    // 3. Render preferences (from cached profile.notePreferences)
    renderProfilePreferences(profile);

    // 4. Render recent templates (from localStorage)
    renderProfileRecentTemplates();

    // 5. Render settings (static action buttons)
    renderProfileSettings(profile);
}

/**
 * Render the profile cover card (avatar, name, role badge).
 * Uses escapeHtml on all dynamic text per code-patterns.md.
 */
export function renderProfileCover(profile) {
    var avatarEl = document.getElementById('profilePageAvatar');
    var nameEl = document.getElementById('profilePageName');
    var roleEl = document.getElementById('profilePageRole');
    var coverEl = document.getElementById('profilePageCoverBg');

    var displayName = profile.displayName || 'Provider';
    var initial = displayName.charAt(0).toUpperCase();
    var color = profile.color || currentProfileColor || '#4A90D9';
    var role = profile.role || 'provider';

    if (avatarEl) {
        avatarEl.textContent = initial;
        avatarEl.style.backgroundColor = color;
    }
    if (nameEl) {
        nameEl.textContent = escapeHtml(displayName);
    }
    if (roleEl) {
        var roleLabel = role === 'admin' ? 'Office Admin' :
                        role === 'dentist' ? 'Dentist' :
                        role === 'hygienist' ? 'Hygienist' :
                        role === 'assistant' ? 'Assistant' : 'Provider';
        roleEl.textContent = escapeHtml(roleLabel);
    }
    if (coverEl) {
        coverEl.style.background = 'linear-gradient(135deg, ' + color + ', ' + color + 'CC)';
    }
}

/**
 * Show a full-page error state on the profile page when profile cannot be loaded.
 * Stays on the profile view â€” never navigates away silently.
 */
export function renderProfilePageError(message) {
    var prefsCard = document.getElementById('profilePrefsCard');
    var templatesCard = document.getElementById('profileTemplatesCard');
    var settingsCard = document.getElementById('profileSettingsCard');
    var nameEl = document.getElementById('profilePageName');
    var roleEl = document.getElementById('profilePageRole');

    // Clear all content areas
    if (prefsCard) prefsCard.innerHTML = '';
    if (templatesCard) templatesCard.innerHTML = '';
    if (settingsCard) settingsCard.innerHTML = '';
    if (roleEl) roleEl.textContent = '';

    // Show error message in the name area and settings card
    if (nameEl) nameEl.textContent = 'Error';
    if (settingsCard) {
        settingsCard.innerHTML =
            '<div class="pa-error-state">' +
                '<div class="pa-error-state-text">' + escapeHtml(message) + '</div>' +
                '<button class="pa-error-retry-btn" data-action="render-provider-profile">&#8635; Retry</button>' +
                '<button class="pa-error-retry-btn" data-action="hide-provider-profile" style="margin-left: 8px;">&#8592; Back</button>' +
            '</div>';
    }
}

/**
 * Render documentation preferences from profile.notePreferences.
 */
export function renderProfilePreferences(profile) {
    var prefsCard = document.getElementById('profilePrefsCard');
    if (!prefsCard) return;

    var prefs = profile.notePreferences || {};
    var defaultType = prefs.defaultAppointmentType || 'other_general';
    var formatPrefs = prefs.formatPreferences || {};

    // Look up appointment type label
    var typeLabel = 'Other / General';
    var typeMatch = state.APPOINTMENT_TYPES.find(function(t) { return t.key === defaultType; });
    if (typeMatch) typeLabel = typeMatch.label;

    // Count configured format preferences
    var configuredFormats = Object.keys(formatPrefs).length;

    var html =
        '<ul class="pa-pref-list">' +
            '<li class="pa-pref-item">' +
                '<span class="pa-pref-label">Default Appointment Type</span>' +
                '<span class="pa-pref-value">' + escapeHtml(typeLabel) + '</span>' +
            '</li>' +
            '<li class="pa-pref-item">' +
                '<span class="pa-pref-label">Custom Format Rules</span>' +
                '<span class="pa-pref-value">' + escapeHtml(String(configuredFormats)) + ' configured</span>' +
            '</li>' +
            '<li class="pa-pref-item">' +
                '<span class="pa-pref-label">Profile Color</span>' +
                '<span class="pa-pref-value" style="display:flex;align-items:center;gap:8px;">' +
                    '<span style="width:14px;height:14px;border-radius:50%;background:' + escapeHtml(profile.color || '#4A90D9') + ';display:inline-block;"></span>' +
                    escapeHtml(profile.color || '#4A90D9') +
                '</span>' +
            '</li>' +
        '</ul>';

    prefsCard.innerHTML = html;
}

/**
 * Render recent appointment templates from localStorage.
 */
export function renderProfileRecentTemplates() {
    var templatesCard = document.getElementById('profileTemplatesCard');
    if (!templatesCard) return;

    var recentTypes = [];
    try {
        recentTypes = getRecentTypes();
    } catch (e) {
        recentTypes = [];
    }

    if (!recentTypes || recentTypes.length === 0) {
        templatesCard.innerHTML =
            '<div class="pa-empty-state">' +
                '<div class="pa-empty-state-icon">&#128203;</div>' +
                '<div class="pa-empty-state-text">No recent templates yet. Start recording to build your history.</div>' +
            '</div>';
        return;
    }

    var html = '<div class="pa-template-list">';
    recentTypes.forEach(function(typeKey) {
        var typeMatch = state.APPOINTMENT_TYPES.find(function(t) { return t.key === typeKey; });
        var label = typeMatch ? typeMatch.shortLabel : typeKey;
        html += '<span class="pa-template-pill">' + escapeHtml(label) + '</span>';
    });
    html += '</div>';

    templatesCard.innerHTML = html;
}


/**
 * Render settings action buttons.
 */
export function renderProfileSettings(profile) {
    var settingsCard = document.getElementById('profileSettingsCard');
    if (!settingsCard) return;

    var html =
        '<button class="pa-action-btn" data-action="profile-page-edit-profile">' +
            '<span class="pa-action-btn-icon">&#9998;</span>' +
            '<span class="pa-action-btn-text">Edit Profile</span>' +
            '<span class="pa-action-btn-arrow">&#8250;</span>' +
        '</button>' +
        '<button class="pa-action-btn" data-action="profile-page-reset-pin">' +
            '<span class="pa-action-btn-icon">&#128274;</span>' +
            '<span class="pa-action-btn-text">Reset PIN</span>' +
            '<span class="pa-action-btn-arrow">&#8250;</span>' +
        '</button>';

    settingsCard.innerHTML = html;
}

/**
 * Open edit profile modal ON TOP of the profile page (no navigation away).
 * Calls existing editProfile() function.
 */
export function profilePageEditProfile() {
    if (!currentProfileId) return;
    openProfileMgmt();
    editProfile(currentProfileId);
}

/**
 * Open reset PIN modal ON TOP of the profile page (no navigation away).
 * Calls existing showResetPin() function.
 */
export function profilePageResetPin() {
    if (!currentProfileId || !currentProfileDisplayName) return;
    openProfileMgmt();
    showResetPin(currentProfileId, currentProfileDisplayName);
}

/**
 * Navigate to the Notes tab from the profile page.
 */
export function showNotesTab() {
    navigateTo('notes');
    loadNotes();
}
